import React, { useState, forwardRef } from 'react';
import { EyeIcon, EyeSlashIcon } from '../icons';

const Input = forwardRef(({ 
  type = 'text',
  label,
  placeholder = '', 
  value, 
  onChange, 
  onFocus, 
  onBlur, 
  error,
  className = '',
  disabled = false,
  showPasswordToggle = false,
  ...props 
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [internalValue, setInternalValue] = useState('');

  const handleFocus = (e) => {
    setIsFocused(true);
    // Call both component's onFocus and react-hook-form's onFocus
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    // Call both component's onBlur and react-hook-form's onBlur
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    // Always call onChange if provided (for react-hook-form)
    if (onChange) {
      onChange(e);
    }
    // Also update internal state if not controlled and not using react-hook-form
    if (value === undefined && !props.name) {
      setInternalValue(newValue);
    }
  };
  const inputType = showPasswordToggle && type === 'password' 
    ? (showPassword ? 'text' : 'password')
    : type;

  const hasError = !!error;

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-[#7B7B7B] dark:text-[#848484] mb-2">
          {label}
        </label>
      )}
      <div 
        className={`relative inline-flex items-center w-full h-12 rounded-xl border transition-all duration-200 ${
          hasError
            ? 'border-red-500 dark:border-red-500'
            : isFocused 
              ? 'border-[#ECECEC] dark:border-[#202020] shadow-sm' 
              : 'border-[#ECECEC] dark:border-[#202020]'
        } ${
          disabled 
            ? 'bg-[#F7F7F7] dark:bg-[#252525] cursor-not-allowed' 
            : 'bg-[#F7F7F7] dark:bg-[#252525] hover:bg-[#F1F1F1] dark:hover:bg-[#2A2A2A]'
        }`}
      >
        {/* Input Field */}
        <input
          ref={ref}
          type={inputType}
          {...(value !== undefined ? { value } : props.name ? {} : { defaultValue: internalValue })}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`flex-1 m-2 p-2 bg-transparent text-sm font-medium placeholder-[#7B7B7B] dark:placeholder-[#848484] text-[#121212] dark:text-[#D0D0D0] focus:outline-none ${
            disabled ? 'cursor-not-allowed' : ''
          } ${showPasswordToggle ? 'pr-10' : ''}`}
          {...props}
        />

        {/* Password Toggle Button */}
        {showPasswordToggle && type === 'password' && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-2 flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#E5E5E5] dark:hover:bg-[#3A3A3A] transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeSlashIcon 
                size={16}
                className="text-[#7B7B7B] dark:text-[#848484]"
              />
            ) : (
              <EyeIcon 
                size={16} 
                className="text-[#7B7B7B] dark:text-[#848484]"
              />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
